// The three plans, in one place.
//
// This file is the seam the payment provider plugs into. When Stripe lands,
// `priceMonthlyBRL` stops being typed here by hand and `stripePriceId` starts
// carrying the id the checkout session is opened with — the page above does not
// change, because it already reads everything from these records.
//
// WHAT IS SETTLED AND WHAT IS NOT, because the difference is the whole reason
// this file exists rather than three hardcoded cards:
//
//   * The tiers and their monthly token caps are decided (docs/crie3-v2.md, D5):
//     starter / pro / scale at 100k / 500k / 2M. Deployments may override the
//     caps through CRIE3_TOKEN_CAP_* without a rebuild, so treat the numbers
//     here as the published default, not as the runtime truth.
//   * The client-brand limit is the agency value metric: every org gets one
//     free brand of its own, and only brands it manages FOR CLIENTS count
//     against the plan. The concept is settled; the per-tier numbers are not.
//   * The PRICES no longer live in this file at all. They are stored by the
//     daemon and fetched at runtime (`usePlanPricing` -> `GET /api/plans`),
//     because a price is a decision the owner makes and re-makes: leaving it
//     here meant editing TypeScript, rebuilding the image and deploying to
//     change one number, which is why the page said "Preço a definir" for
//     months. `priceMonthlyBRL` and `clientBrands` in the records below are the
//     shipped fallback — what a visitor sees before the fetch lands, or when
//     nobody has set anything — and `planCatalogueWithPricing` lays the stored
//     values over them.
//
// Anything not settled is `null` here, and the page renders the absence rather
// than a number nobody chose. A sales page that invents a price is worse than
// one that admits it does not have it yet: the invented one gets screenshotted.

import type { Crie3PlanId, PlanPricing } from '@open-design/contracts';

// The id union lives in `packages/contracts` because the daemon stores a row
// per tier and both sides must agree on the id space. Re-exported here so the
// existing importers of this module keep working.
export type { Crie3PlanId } from '@open-design/contracts';

export interface Crie3Plan {
  id: Crie3PlanId;
  name: string;
  /** One line on who the tier is for. */
  tagline: string;
  /** Published monthly token cap (D5). */
  tokenCap: number;
  /**
   * Client brands the tier may manage, beyond the org's own free brand.
   * `null` until the owner sets it; the card omits the line entirely.
   */
  clientBrands: number | null;
  /**
   * Monthly price in BRL. `null` until the owner sets it; the card shows
   * "a definir" and its button becomes a conversation rather than a checkout.
   */
  priceMonthlyBRL: number | null;
  /** Filled when the payment provider is wired. */
  stripePriceId: string | null;
  /** What this tier adds over the one before it. */
  highlights: string[];
  /** The middle tier carries the visual emphasis, as it should. */
  featured?: boolean;
}

/** Capabilities every tier includes — the product itself, not a tier. */
export const PLAN_BASELINE: string[] = [
  'Posts, carrosséis e stories a partir de uma conversa',
  'Envio do logo, das fotos e do material da marca para orientar a arte',
  'Geração de imagem, áudio e vídeo já inclusa',
  'Sistemas de design próprios, para a peça sair na cara da marca',
  'Publicação assistida no Instagram, Facebook e TikTok',
  'Memória do que a marca é, entre uma conversa e outra',
];

export const CRIE3_PLANS: Crie3Plan[] = [
  {
    id: 'starter',
    name: 'Starter',
    tagline: 'Para quem cuida da comunicação de uma igreja.',
    tokenCap: 100_000,
    clientBrands: null,
    priceMonthlyBRL: null,
    stripePriceId: null,
    highlights: ['Sua própria marca, sempre inclusa'],
  },
  {
    id: 'pro',
    name: 'Pro',
    tagline: 'Para o freelancer que atende alguns ministérios.',
    tokenCap: 500_000,
    clientBrands: null,
    priceMonthlyBRL: null,
    stripePriceId: null,
    highlights: ['Marcas de cliente separadas, cada uma com seu material'],
    featured: true,
  },
  {
    id: 'scale',
    name: 'Scale',
    tagline: 'Para a agência com uma carteira de clientes.',
    tokenCap: 2_000_000,
    clientBrands: null,
    priceMonthlyBRL: null,
    stripePriceId: null,
    highlights: ['Volume para atender a carteira inteira no mesmo mês'],
  },
];

/**
 * The full noun phrase, because the grammar changes with the magnitude:
 * `100 mil tokens` but `2 milhões DE tokens`. Returning only the number and
 * appending " tokens" in the markup produced "2 milhões tokens" — the kind of
 * sentence that tells a visitor the page was assembled, not written.
 */
export function formatTokenAllowance(tokens: number): string {
  if (tokens >= 1_000_000) {
    const millions = tokens / 1_000_000;
    const value = Number.isInteger(millions) ? String(millions) : millions.toFixed(1).replace('.', ',');
    return `${value} ${millions === 1 ? 'milhão' : 'milhões'} de tokens`;
  }
  return `${Math.round(tokens / 1000)} mil tokens`;
}

export function formatPriceBRL(price: number): string {
  return price.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: Number.isInteger(price) ? 0 : 2,
  });
}

/** Whether any tier has a price yet — decides the page's whole CTA posture. */
export function hasPublishedPricing(plans: Crie3Plan[] = CRIE3_PLANS): boolean {
  return plans.some((plan) => plan.priceMonthlyBRL !== null);
}

/**
 * The shipped catalogue with the operator's stored prices laid over it.
 *
 * The two halves are deliberately separate. What a tier IS — its name, who it
 * is for, what it adds, its published token cap — ships with a release and is
 * reviewed like any other copy. What it COSTS is a decision the owner makes and
 * re-makes, so it lives on the server and arrives here at runtime. Before the
 * fetch lands (`pricing === null`) the catalogue is returned untouched, which
 * means every tier reads "Preço a definir" — the same honest state as a
 * deployment where nobody has set a price yet.
 */
export function planCatalogueWithPricing(pricing: PlanPricing[] | null): Crie3Plan[] {
  if (!pricing) return CRIE3_PLANS;
  const byId = new Map(pricing.map((row) => [row.id, row]));
  return CRIE3_PLANS.map((plan) => {
    const row = byId.get(plan.id);
    if (!row) return plan;
    return { ...plan, priceMonthlyBRL: row.priceMonthlyBRL, clientBrands: row.clientBrands };
  });
}
